import React from 'react';
import { Link } from 'react-router-dom';
import { QrCodeIcon, Shield, Lock, Eye, Database, UserCheck, Globe } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-gray-900">Home</Link>
              <Link to="/auth" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Sign In</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Privacy Policy
            <span className="block text-yellow-300 text-3xl md:text-4xl mt-4">Your Privacy is Our Priority</span>
          </h1>
          <p className="text-xl mb-4">Last updated: January 1, 2025</p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <UserCheck className="h-6 w-6 mr-2 text-blue-600" />
              Introduction
            </h2>
            <p className="text-gray-600 leading-relaxed">
              At iComment.app, we are committed to protecting your privacy and ensuring the security of your personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
              anonymous messaging service.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Database className="h-6 w-6 mr-2 text-green-600" />
              Information We Collect
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Account Information</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Email address (for account creation and notifications)</li>
                  <li>Phone number (optional, for SMS notifications)</li>
                  <li>Username and password</li>
                  <li>Subscription and billing information</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">QR Code Data</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>QR code titles and greeting messages</li>
                  <li>QR code generation timestamps</li>
                  <li>Usage analytics (number of scans, messages sent)</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Message Content</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Message text content</li>
                  <li>Optional sender contact information (if provided by sender)</li>
                  <li>Message timestamps</li>
                  <li>Location data (if shared by sender)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Eye className="h-6 w-6 mr-2 text-purple-600" />
              How We Use Your Information
            </h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>To provide and maintain the iComment.app service</li>
              <li>To send email and SMS notifications about messages</li>
              <li>To process payments and manage subscriptions</li>
              <li>To provide customer support</li>
              <li>To improve our service through analytics</li>
              <li>To prevent fraud and abuse</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          {/* Privacy Protection */}
          <section className="bg-blue-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Lock className="h-6 w-6 mr-2 text-blue-600" />
              How We Protect Your Privacy
            </h2>
            <div className="space-y-3">
              <p className="text-gray-600">
                <strong className="text-gray-900">Anonymous Messaging:</strong> We never share your personal contact 
                information with message senders. All communication is routed through our platform.
              </p>
              <p className="text-gray-600">
                <strong className="text-gray-900">Data Encryption:</strong> All data is encrypted in transit and at rest 
                using industry-standard encryption protocols.
              </p>
              <p className="text-gray-600">
                <strong className="text-gray-900">Limited Access:</strong> Only authorized personnel have access to user data, 
                and only when necessary for service operation or support.
              </p>
              <p className="text-gray-600">
                <strong className="text-gray-900">No Third-Party Sharing:</strong> We do not sell, rent, or share your 
                personal information with third parties for marketing purposes.
              </p>
            </div>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Globe className="h-6 w-6 mr-2 text-red-600" />
              When We Share Information
            </h2>
            <p className="text-gray-600 mb-4">We may share your information only in the following limited circumstances:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>With service providers who help us operate the platform (email services, payment processors)</li>
              <li>When required by law or legal process</li>
              <li>To protect the rights, property, or safety of iComment.app, our users, or others</li>
              <li>In connection with a merger, acquisition, or sale of assets (with user notification)</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Access & Control</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li>View and download your data</li>
                  <li>Update account information</li>
                  <li>Delete QR codes and messages</li>
                  <li>Close your account</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Privacy Controls</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                  <li>Opt out of optional communications</li>
                  <li>Block unwanted message senders</li>
                  <li>Control location sharing</li>
                  <li>Manage notification preferences</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-600 mb-4">
              We retain your personal information for as long as your account is active or as needed to provide services. 
              We may also retain and use information as necessary to comply with legal obligations, resolve disputes, 
              and enforce agreements.
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>Account data: Retained until account deletion</li>
              <li>Messages: You can delete at any time</li>
              <li>QR codes: Retained until manually deleted</li>
              <li>Usage analytics: Aggregated data may be retained indefinitely</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-600">
              iComment.app is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If you are a parent or guardian and believe your 
              child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions or Concerns?</h2>
            <p className="text-gray-600 mb-4">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="space-y-2 text-gray-600">
              <p><strong>Email:</strong> privacy@icomment.app</p>
              <p><strong>Address:</strong> 123 Tech Street, San Francisco, CA 94105</p>
              <p><strong>Support:</strong> <Link to="/contact" className="text-blue-600 hover:underline">Contact Form</Link></p>
            </div>
          </section>

          {/* Updates */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Policy Updates</h2>
            <p className="text-gray-600">
              We may update this Privacy Policy from time to time. We will notify you of any material changes 
              by posting the new policy on this page and updating the "Last updated" date. We encourage you 
              to review this policy periodically.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}